import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Mail,
  Shield,
  Loader2,
  CheckCircle2,
  XCircle,
  Unplug,
  LogIn,
  Zap,
  ClipboardCheck,
} from "lucide-react";
import {
  useGmailConnectionStatus,
  useConnectGmail,
  useDisconnectGmail,
} from "@/hooks/useGmailAuth";
import {
  useGmailSettings,
  useUpdateGmailSettings,
} from "@/hooks/useGmailEmails";

export const GmailSettings: React.FC = () => {
  const { data: status, isLoading: statusLoading } = useGmailConnectionStatus();
  const connectGmail = useConnectGmail();
  const disconnectGmail = useDisconnectGmail();
  const { data: settings, isLoading: settingsLoading } = useGmailSettings();
  const updateSettings = useUpdateGmailSettings();

  const autoCreateEnabled = settings?.autoCreateEnabled ?? false;

  const handleConnect = async () => {
    try {
      await connectGmail.mutateAsync();
    } catch {
      // Error handled in hook
    }
  };

  const handleModeChange = (enabled: boolean) => {
    updateSettings.mutate({ autoCreateEnabled: enabled });
  };

  if (statusLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Gmail Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Connect your Gmail account to receive and process quotation requests automatically.
        </p>
      </div>

      {/* Gmail Connection Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Mail className="h-5 w-5" />
            Gmail Connection
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <p className="text-sm font-medium">Connection Status</p>
              {status?.connected ? (
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-muted-foreground">{status.gmailEmail}</span>
                  <Badge variant="outline" className="text-green-600 border-green-300">
                    Connected
                  </Badge>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-red-400" />
                  <span className="text-sm text-muted-foreground">Not connected</span>
                  <Badge variant="outline" className="text-red-500 border-red-300">
                    Disconnected
                  </Badge>
                </div>
              )}
            </div>

            {status?.connected ? (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2">
                    <Unplug className="h-4 w-4" />
                    Disconnect
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Disconnect Gmail</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to disconnect your Gmail account? You will no longer
                      receive quotation emails from Gmail until you reconnect.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => disconnectGmail.mutate()}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Disconnect
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ) : (
              <Button
                onClick={handleConnect}
                disabled={connectGmail.isPending}
                className="gap-2"
                size="sm"
              >
                {connectGmail.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <LogIn className="h-4 w-4" />
                )}
                {connectGmail.isPending ? "Connecting..." : "Connect Gmail"}
              </Button>
            )}
          </div>

          {!status?.connected && (
            <div className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
              <div className="flex gap-2">
                <Shield className="h-4 w-4 mt-0.5 shrink-0" />
                <p>
                  QuickBill uses read-only access to your Gmail. We never send emails on your behalf
                  through Gmail — quotation replies are sent via your company SMTP settings.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quotation Creation Mode Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ClipboardCheck className="h-5 w-5" />
            Quotation Creation Mode
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {settingsLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading settings...
            </div>
          ) : (
            <>
              {/* Mode Switcher */}
              <div className="inline-flex w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 p-1 gap-1">
                <button
                  type="button"
                  disabled={updateSettings.isPending}
                  onClick={() => handleModeChange(false)}
                  className={`flex-1 rounded-md px-4 py-2.5 text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                    !autoCreateEnabled
                      ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 shadow-sm"
                      : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
                  }`}
                >
                  <ClipboardCheck className="h-4 w-4" />
                  Review &amp; Create
                </button>
                <button
                  type="button"
                  disabled={updateSettings.isPending}
                  onClick={() => handleModeChange(true)}
                  className={`flex-1 rounded-md px-4 py-2.5 text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                    autoCreateEnabled
                      ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 shadow-sm"
                      : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
                  }`}
                >
                  <Zap className="h-4 w-4" />
                  Auto Create
                </button>
              </div>

              {updateSettings.isPending && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Saving...
                </div>
              )}

              {/* Mode description */}
              {!autoCreateEnabled ? (
                <div className="text-sm rounded-md border border-blue-200 bg-blue-50 dark:border-blue-800/50 dark:bg-blue-950/20 p-3 text-blue-800 dark:text-blue-300">
                  <p className="font-semibold mb-1.5 flex items-center gap-1.5">
                    <ClipboardCheck className="h-4 w-4" />
                    Review &amp; Create Mode
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-blue-700 dark:text-blue-400">
                    <li>After AI classifies an email as a quotation request, you will review it manually</li>
                    <li>You choose how to match the extracted buyer and items to your existing records</li>
                    <li>New buyers or items can be optionally saved to your master lists</li>
                    <li>You fill in the Quotation Form before the quotation is created</li>
                    <li>Best for when you need to verify extracted data before committing</li>
                  </ul>
                </div>
              ) : (
                <div className="text-sm rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800/50 dark:bg-amber-950/20 p-3 text-amber-800 dark:text-amber-300">
                  <p className="font-semibold mb-1.5 flex items-center gap-1.5">
                    <Zap className="h-4 w-4" />
                    Auto Create Mode
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-amber-700 dark:text-amber-400">
                    <li>
                      After classification, quotations are created <strong>automatically</strong> without
                      any manual review step
                    </li>
                    <li>
                      <strong>Exact match</strong> on buyer email or item code → existing record is used
                    </li>
                    <li>
                      <strong>Fuzzy / possible match</strong> → the best candidate is used automatically
                    </li>
                    <li>
                      <strong>No match</strong> → a new buyer or item is created in your master lists
                      with extracted data (TIN, registration, tax details will need manual completion)
                    </li>
                    <li>
                      Quotation validity defaults to <strong>30 days</strong> from the date of creation
                    </li>
                    <li>Created quotations appear directly in the Quotations page as Draft</li>
                    <li>
                      Use this mode when you trust the AI extraction quality and process high email
                      volumes
                    </li>
                  </ul>
                  <div className="mt-2 pt-2 border-t border-amber-200 dark:border-amber-800 text-xs text-amber-600 dark:text-amber-500">
                    <strong>Note:</strong> Auto-created quotations start as Draft. Review and complete
                    any missing details (TIN, registration numbers, tax types) before sending to
                    customers.
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GmailSettings;
